import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  type RouterHistory,
} from '@tanstack/react-router'
import { ErrorState } from './components/chrome'
import { PublicLayout } from './components/public-layout'
import { ShellLayout } from './components/shell-layout'
import { useStaffSessionSync } from './lib/session'
import { CatalogPage } from './routes/catalog'
import { CompaniesPage } from './routes/companies'
import { CompanyPage } from './routes/company'
import { HomePage } from './routes/home'
import { NotFound } from './routes/not-found'
import { PasskeyPage } from './routes/passkey'
import { SignInPage, validateSignInSearch } from './routes/sign-in'

/*
 * The back office's own route tree (ADR 0008 §2), apart from the portal's: the sign-in with the
 * email link (/entrar), the passkey (/clave), and the panel with the screens of N-a.2 (ADR 0008
 * §3): «Inicio» (/), «Empresas» (/empresas and a company's detail) and «Cursos» (/cursos), with
 * the catalogue's course editor (/cursos/<id>/editar, ADR 0009 §1) and the company wizard
 * (/empresas/nueva, ADR 0009 §7).
 */

function Root() {
  useStaffSessionSync()
  return <Outlet />
}

const rootRoute = createRootRoute({ component: Root, notFoundComponent: NotFound })

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_public',
  component: PublicLayout,
})
const signInRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/entrar',
  validateSearch: validateSignInSearch,
  component: SignInPage,
})
const passkeyRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/clave',
  component: PasskeyPage,
})

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_shell',
  component: ShellLayout,
})
const homeRoute = createRoute({ getParentRoute: () => shellRoute, path: '/', component: HomePage })
const companiesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/empresas',
  component: CompaniesPage,
})
const companyRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/empresas/$organizationId',
  component: CompanyPage,
})
// Loaded on demand, as the editor: only for adding a company. A static path, so it is never
// taken for a company's id.
const companyWizardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/empresas/nueva',
  component: lazyRouteComponent(() => import('./routes/company-wizard'), 'CompanyWizardPage'),
  errorComponent: ({ reset }) => <ErrorState onRetry={reset} />,
})
const catalogRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/cursos',
  component: CatalogPage,
})

// Loaded on demand: the editor and the lesson view of its preview are only for this page.
const courseEditorRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/cursos/$courseId/editar',
  component: lazyRouteComponent(() => import('./routes/course-editor'), 'CourseEditorPage'),
  // Its code could not be loaded (a connection lost, or a new version deployed meanwhile).
  errorComponent: ({ reset }) => <ErrorState onRetry={reset} />,
})

const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([signInRoute, passkeyRoute]),
  shellRoute.addChildren([
    homeRoute,
    companiesRoute,
    companyWizardRoute,
    companyRoute,
    catalogRoute,
    courseEditorRoute,
  ]),
])

/** The back office's router; the tests give it a memory history instead of the browser's. */
export function createBackOfficeRouter(history?: RouterHistory) {
  return createRouter({ routeTree, history, scrollRestoration: true })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createBackOfficeRouter>
  }
}
